import { getAuthIdToken } from './auth';

export type UserSub = string | null | undefined;

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export type HttpError = Error & {
  response?: Response;
  status?: number;
};

interface ApiRequestOptions {
  auth?: boolean;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

const getToken = async () => (await getAuthIdToken()).getJwtToken();

const apiRequest = async <T = unknown>(
  endpoint: string,
  userSub: UserSub = null,
  method: HttpMethod = 'GET',
  body?: unknown,
  options: ApiRequestOptions = {}
): Promise<T> => {
  const { auth = true, signal, headers: extraHeaders } = options;

  const headers: Record<string, string> = {};

  if (auth) headers.Authorization = await getToken();
  if (userSub) headers['user-sub'] = userSub;
  if (extraHeaders) Object.assign(headers, extraHeaders);

  const params: RequestInit = {
    headers,
    method: method,
  };
  if (signal) params.signal = signal;
  if (method !== 'GET' && !!body) {
    params.body = JSON.stringify(body);
  }

  return await fetch(endpoint, params)
    .then(async (res) => {
      if (res.status !== 200) {
        if (!res?.ok) {
          const text = await res.text();
          let resultObject: { message?: string } | undefined;
          try {
            resultObject = JSON.parse(text) as { message?: string };
          } catch {
            // Response is not JSON (e.g., HTML error page)
            const err: HttpError = new Error('Unable to load data. Please try again later.');
            err.response = res;
            err.status = res.status;
            throw err;
          }
          const err: HttpError = new Error(resultObject?.message ?? 'There was a request error');
          err.response = res;
          err.status = res.status;
          throw err;
        }
      }
      const text = await res.text();
      if (!text) {
        return undefined as T;
      }
      try {
        return JSON.parse(text) as T;
      } catch {
        return text as T;
      }
    })
    .catch((e: HttpError) => {
      if (e.status !== 404) {
        console.error(e);
      }
      throw e;
    });
};

export default apiRequest;
